"use client";
import { formatSavedTimeAgo } from "@/lib/time";
import { cn } from "@/lib/utils";
import type { Property } from "@/schemas/property";
import * as Icons from "lucide-react";
import { Bath, Bed, ChevronRight, Ruler } from "lucide-react";
import Image from "next/image";

interface PropertyCardProps {
  property: Property;
  savedPropertyId?: string;
  isSelected?: boolean;
  selectable?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  variant?: "default" | "compact";
}

export function PropertyCard({
  property,
  savedPropertyId,
  isSelected = false,
  selectable = false,
  onRemove,
  onClick,
  variant = "default",
}: PropertyCardProps) {
  const isCompact = variant === "compact";

  return (
    <div className="group relative">
      <div
        onClick={onClick}
        className={cn(
          "cursor-pointer overflow-hidden rounded-lg border bg-white shadow-sm transition hover:shadow-md",
          isSelected && "ring-2 ring-primary ring-offset-2",
          selectable &&
            "hover:ring-2 hover:ring-primary/50 hover:ring-offset-2",
        )}
        aria-label="View property"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onClick?.();
          }
        }}
      >
        <div className={cn("relative", isCompact ? "h-48" : "h-56")}>
          {property.images?.[0] ? (
            <Image
              src={property.images[0]}
              alt={property.address}
              fill
              className="object-cover"
            />
          ) : (
            <div className="w-full h-full bg-black/10 flex items-center justify-center">
              <Icons.Home className="h-6 w-6 text-black/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

          {/* Source Logo */}
          {property.source !== "manual" && (
            <div className="absolute top-3 left-3">
              <div className="bg-white rounded-lg p-1.5 shadow-sm">
                <img
                  src={`https://logo.clearbit.com/${property.source}`}
                  alt={property.source}
                  className="w-5 h-5 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='2' y='2' width='20' height='20' rx='2' ry='2'%3E%3C/rect%3E%3Cpath d='M12 6v12'%3E%3C/path%3E%3Cpath d='M6 12h12'%3E%3C/path%3E%3C/svg%3E";
                    e.currentTarget.className = "w-4 h-4 text-gray-400";
                  }}
                />
              </div>
            </div>
          )}

          {/* Add posting time - refined version */}
          {property.created_at && (
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <div className="flex justify-end">
                <span
                  className={cn(
                    "text-white font-medium bg-black/40 backdrop-blur-sm",
                    "px-2.5 py-1 rounded-full",
                    "text-xs tracking-wide",
                    "transition-opacity duration-200",
                    "group-hover:opacity-100",
                    isCompact ? "opacity-70" : "opacity-85",
                  )}
                >
                  {formatSavedTimeAgo(property.created_at)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1 min-w-0">
              <h3
                className={cn(
                  "font-semibold text-gray-900 line-clamp-1",
                  isCompact ? "text-lg" : "text-xl",
                )}
              >
                {property.address}
              </h3>
              <div
                className={cn(
                  "font-semibold text-gray-900",
                  isCompact ? "text-lg" : "text-2xl",
                )}
              >
                ${property.price.toLocaleString()}
              </div>
            </div>
            <div className="text-gray-400 group-hover:text-canary transition-colors">
              <ChevronRight className={cn(isCompact ? "w-4 h-4" : "w-5 h-5")} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {property.bedrooms && (
              <div className="flex items-center gap-1">
                <Bed
                  className={cn(
                    "text-gray-500",
                    isCompact ? "w-3.5 h-3.5" : "w-4 h-4",
                  )}
                />
                <span
                  className={cn(
                    "text-gray-600",
                    isCompact ? "text-xs" : "text-sm",
                  )}
                >
                  {property.bedrooms} beds
                </span>
              </div>
            )}
            {property.bathrooms && (
              <div className="flex items-center gap-1">
                <Bath
                  className={cn(
                    "text-gray-500",
                    isCompact ? "w-3.5 h-3.5" : "w-4 h-4",
                  )}
                />
                <span
                  className={cn(
                    "text-gray-600",
                    isCompact ? "text-xs" : "text-sm",
                  )}
                >
                  {property.bathrooms} baths
                </span>
              </div>
            )}
            {property.square_feet && (
              <div className="flex items-center gap-1">
                <Ruler
                  className={cn(
                    "text-gray-500",
                    isCompact ? "w-3.5 h-3.5" : "w-4 h-4",
                  )}
                />
                <span
                  className={cn(
                    "text-gray-600",
                    isCompact ? "text-xs" : "text-sm",
                  )}
                >
                  {property.square_feet.toLocaleString()} sqft
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
